import flattenDeep from "lodash/flatten";
import type { TLayoutRoute, TRoute } from "@/shared/types/route.ts";
import { Route, Routes as ReactRoutes } from "react-router-dom";
import ProtectedRoute from "@/shared/routes/protected-route.tsx";
import PersistLogin from "@/shared/routes/persist-login.tsx";

export const generateFlattenRoutes = (routes?: (TRoute | TLayoutRoute)[]): TRoute[] => {
  if (!routes) return [];
  return flattenDeep(
    routes.map(({ routes: subRoutes, ...rest }) => [rest, ...generateFlattenRoutes(subRoutes)]),
  ) as TRoute[];
};

// Helper function to render nested routes recursively
const renderNestedRoutes = (routes: TRoute[], parentKey: string = ''): JSX.Element[] => {
  return routes.map((route, i) => {
    const { component: Component, path, name, roles, routes: nestedRoutes } = route;
    const key = `${parentKey}-${name || `route-${i}`}`;

    // If this route has nested routes (like SettingsLayout), render it as a layout
    if (nestedRoutes && nestedRoutes.length > 0) {
      return (
        <Route key={key} path={path} element={Component ? <Component /> : null}>
          {renderNestedRoutes(nestedRoutes, key)}
        </Route>
      );
    }

    // Handle index routes (empty path means index)
    if (Component && path === "") {
      return <Route key={key} index element={<Component />} />;
    }

    // Regular route with protection if roles are specified
    if (Component && path && roles && roles.length > 0) {
      return (
        <Route key={`protected-${key}`} element={<ProtectedRoute roles={roles} />}>
          <Route element={<Component />} path={path} />
        </Route>
      );
    }

    // Simple route without protection
    return Component && path ? <Route key={key} element={<Component />} path={path} /> : null;
  }).filter(Boolean) as JSX.Element[];
};

export const renderRoutes = (mainRoutes: TLayoutRoute[]) => {
  return () => {
    const layouts = mainRoutes.map(({ layout: Layout, routes }, index) => {
      return (
        <Route key={index} element={<Layout key={index} />}>
          {renderNestedRoutes(routes, `layout-${index}`)}
        </Route>
      );
    });

    return (
      <ReactRoutes>
        <Route key={"persist"} path={"/"} element={<PersistLogin />}>
          {layouts}
        </Route>
      </ReactRoutes>
    );
  };
};
